import {useSendManagerAmplitudeTestMutation} from '@/generated/graphql';
import {useLogAppUse} from '@/hooks/useLogAppUse';
import {useStartReplay} from '@landlord/hooks/useStartReplay';
import {useTrackPageView} from '@landlord/hooks/useTrackPageView';
import LandlordLayout from '@/layout/LandlordLayout';
import {SentryRoutes} from '@/main';
import {track} from '@amplitude/analytics-browser';
import {userProfileContext} from '@ark/web';
import {
  DocumentGenerationGuard,
  DocumentGenerationPaused,
} from '@landlord/components/DocumentGenerationPaused';
import React, {useEffect} from 'react';
import lazyWithPreload from 'react-lazy-with-preload';
import {Navigate, Route} from 'react-router-dom';
import {ParamsToUUIDNavigator} from '../ParamsToUUIDNavigator';
import {importWithRetry, preloadAll} from '@/utils/chunkReloadOperations';

const SigningLanding = lazyWithPreload(() =>
  importWithRetry(
    () => import('@landlord/pages/Signing/LandlordSigning/LandlordSigning'),
  ),
);
const SaveASTPage = lazyWithPreload(() =>
  importWithRetry(
    () => import('@landlord/pages/GeneratedAgreement/SaveASTPage/SaveASTPage'),
  ),
);
const PropertiesPage = lazyWithPreload(() =>
  importWithRetry(() =>
    import('@landlord/pages/Properties/PropertiesPage').then(module => ({
      default: module.PropertiesPage,
    })),
  ),
);
const Section21Details = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/Documents/Section21/Details')),
);
const Documents = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/Documents/Documents')),
);
const Payments = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/Payments/Payments')),
);
const StripeRefresh = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/Payments/StripeRefresh')),
);

const News = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/News/NewsPage')),
);
const Profile = lazyWithPreload(() =>
  importWithRetry(
    () => import('@landlord/pages/LandlordProfile/LandlordProfile'),
  ),
);
const PropertyDetails = lazyWithPreload(() =>
  importWithRetry(() =>
    import('@landlord/pages/Properties/PropertyDetails/PropertyDetails').then(
      module => ({default: module.PropertyDetails}),
    ),
  ),
);
const Compliance = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/Compliance/Compliance')),
);
const Conversations = lazyWithPreload(() =>
  importWithRetry(() => import('@landlord/pages/Conversations/Conversations')),
);
// const Tenants = lazyWithPreload(
//   () => importWithRetry(() => import('@landlord/pages/Tenants/Tenants')),
// );
const ApplicantDetails = lazyWithPreload(() =>
  importWithRetry(() =>
    import(
      '@/pages/ENT/Referencing/components/ApplicantDetails/ApplicantDetails'
    ).then(module => ({
      default: module.ApplicantDetails,
    })),
  ),
);

const Home = lazyWithPreload(() =>
  importWithRetry(() =>
    import('@landlord/pages/Home/Home').then(module => ({
      default: module.Home,
    })),
  ),
);

const TenancyApplications = lazyWithPreload(() =>
  importWithRetry(() =>
    import('@landlord/pages/TenancyApplications').then(module => ({
      default: module.TenancyApplications,
    })),
  ),
);

const componentsToPreload = [
  SigningLanding,
  SaveASTPage,
  PropertiesPage,
  Section21Details,
  Documents,
  Payments,
  StripeRefresh,
  News,
  Profile,
  PropertyDetails,
  Compliance,
  Conversations,
];

const LandlordRoot: React.FC = () => {
  useStartReplay();
  useTrackPageView();
  const [sendBackendEvent] = useSendManagerAmplitudeTestMutation();
  const {email} = React.useContext(userProfileContext);

  useEffect(() => {
    if (email) {
      track('Adblock Test MD Browser');
      sendBackendEvent({variables: {input: {}}});
      preloadAll(componentsToPreload);
    }
  }, [sendBackendEvent, email]);

  useLogAppUse();

  return (
    <SentryRoutes>
      <Route element={<LandlordLayout />}>
        <Route path="/home" element={<Home />} />
        <Route
          path="/properties/:homeId/tenancy/:tenancyId/:action"
          element={
            <ParamsToUUIDNavigator parameterNames={['homeId', 'tenancyId']}>
              <PropertyDetails />
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/properties/:homeId/tenancy/:tenancyId"
          element={
            <ParamsToUUIDNavigator parameterNames={['homeId', 'tenancyId']}>
              <PropertyDetails />
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/properties/:homeId"
          element={
            <ParamsToUUIDNavigator parameterNames={['homeId']}>
              <PropertyDetails />
            </ParamsToUUIDNavigator>
          }
        />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route
          path="/news/:itemId"
          element={
            <ParamsToUUIDNavigator parameterNames={['itemId']}>
              <News />
            </ParamsToUUIDNavigator>
          }
        />
        <Route path="/news" element={<News />} />
        <Route
          path="/documents/tenancy-agreements/:id/:tab"
          element={
            <ParamsToUUIDNavigator parameterNames={['id']}>
              <DocumentGenerationGuard documentType="tenancy-agreement">
                <SaveASTPage />
              </DocumentGenerationGuard>
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/documents/tenancy-agreements/:id"
          element={
            <ParamsToUUIDNavigator parameterNames={['id']}>
              <DocumentGenerationGuard documentType="tenancy-agreement">
                <SaveASTPage />
              </DocumentGenerationGuard>
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/documents/section-21/:id"
          element={
            <ParamsToUUIDNavigator parameterNames={['id']}>
              <DocumentGenerationGuard documentType="section-21">
                <Section21Details />
              </DocumentGenerationGuard>
            </ParamsToUUIDNavigator>
          }
        />
        <Route path="/documents" element={<Documents />} />
        <Route
          path="/landlord-sign/:agreementUUID/:signatureUUID"
          element={
            <ParamsToUUIDNavigator
              parameterNames={['agreementUUID', 'signatureUUID']}
            >
              <SigningLanding />
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/agreements/:id"
          element={
            <DocumentGenerationPaused documentType="tenancy-agreement" />
          }
        />
        <Route path="/payments/stripe-refresh" element={<StripeRefresh />} />
        <Route
          path="/payments/tenancy/:residencyId"
          element={
            <ParamsToUUIDNavigator parameterNames={['residencyId']}>
              <Payments />
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/payments/:subscriptionId"
          element={
            <ParamsToUUIDNavigator parameterNames={['subscriptionId']}>
              <Payments />
            </ParamsToUUIDNavigator>
          }
        />
        <Route path="/payments" element={<Payments />} />
        <Route
          path="/messages/:conversationId"
          element={
            <ParamsToUUIDNavigator parameterNames={['conversationId']}>
              <Conversations />
            </ParamsToUUIDNavigator>
          }
        />
        <Route path="/messages" element={<Conversations />} />
        <Route path="/profile" element={<Profile />} />
        <Route
          path="/compliance/:complianceItemId/add"
          element={
            <ParamsToUUIDNavigator parameterNames={['complianceItemId']}>
              <Compliance sideBarMode="add" />
            </ParamsToUUIDNavigator>
          }
        />
        <Route
          path="/compliance/:complianceItemId"
          element={
            <ParamsToUUIDNavigator parameterNames={['complianceItemId']}>
              <Compliance />
            </ParamsToUUIDNavigator>
          }
        />
        <Route path="/compliance" element={<Compliance />} />
        <Route
          path="/tenant-reference-report/:tenantReferenceUUID"
          element={
            <ParamsToUUIDNavigator parameterNames={['tenantReferenceUUID']}>
              <ApplicantDetails />
            </ParamsToUUIDNavigator>
          }
        />
        {/* removing until we figure out what we are doing with the tenants list */}
        {/* <Route path="/tenants" element={<Tenants />} /> */}
        <Route path="/tenancy-applications" element={<TenancyApplications />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </SentryRoutes>
  );
};

export default LandlordRoot;
